import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'core-ui-input-otp',
  templateUrl: './input-otp.component.html',
  styleUrls: ['./input-otp.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputOtpComponent),
      multi: true,
    },
  ],
})
export class InputOtpComponent implements ControlValueAccessor, AfterViewInit {
  @Input() disabled = false;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';

  @Output() completed = new EventEmitter<string>();

  @ViewChildren('otpInput') inputs!: QueryList<ElementRef<HTMLInputElement>>;

  private readonly length = 6;
  digits: string[] = Array(this.length).fill('');

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    this.syncDomWithDigits();
  }

  onInput(event: Event, index: number): void {
    const inputEl = event.target as HTMLInputElement;
    let value = (inputEl.value || '').replace(/\D/g, '');

    if (value.length > 1) {
      value = value[0];
    }

    inputEl.value = value;
    this.digits[index] = value || '';

    this.toggleFilledClass(inputEl);

    if (value && index < this.length - 1) {
      this.focusInput(index + 1);
    }

    this.emitValue();
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const inputEl = event.target as HTMLInputElement;

    if (event.key === 'Backspace') {
      event.preventDefault();
      if (this.digits[index]) {
        this.digits[index] = '';
        inputEl.value = '';
        this.toggleFilledClass(inputEl);
      } else if (index > 0) {
        this.focusInput(index - 1);
        const prev = this.inputs.get(index - 1)?.nativeElement;
        if (prev) {
          prev.value = '';
          this.digits[index - 1] = '';
          this.toggleFilledClass(prev);
        }
      }
      this.emitValue();
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusInput(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < this.length - 1) {
      event.preventDefault();
      this.focusInput(index + 1);
      return;
    }
  }

  onPaste(event: ClipboardEvent, index: number): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';
    const clean = text.replace(/\D/g, '');

    if (!clean) {
      return;
    }

    let cursor = index;
    for (let i = 0; i < clean.length && cursor < this.length; i++, cursor++) {
      const char = clean[i];
      this.digits[cursor] = char;

      const inputEl = this.inputs.get(cursor)?.nativeElement;
      if (inputEl) {
        inputEl.value = char;
        this.toggleFilledClass(inputEl);
      }
    }

    if (cursor - 1 >= 0) {
      this.focusInput(cursor - 1);
    }

    this.emitValue();
  }

  onFocus(index: number): void {
    const inputEl = this.inputs.get(index)?.nativeElement;
    if (inputEl) {
      inputEl.select();
    }
    this.onTouched();
  }

  private focusInput(index: number): void {
    const inputEl = this.inputs.get(index)?.nativeElement;
    if (inputEl && !this.disabled) {
      inputEl.focus();
    }
  }

  private toggleFilledClass(inputEl: HTMLInputElement): void {
    if (inputEl.value) {
      inputEl.classList.add('filled');
    } else {
      inputEl.classList.remove('filled');
    }
  }

  private emitValue(): void {
    const value = this.digits.join('');
    //const allFilled = this.digits.every((d) => d && d.length === 1);

    this.onChange(value);

    /*if (allFilled) {
            this.completed.emit(value);
        }*/
  }

  private syncDomWithDigits(): void {
    if (!this.inputs || !this.inputs.length) return;

    this.inputs.forEach((ref, index) => {
      const el = ref.nativeElement;
      const val = this.digits[index] ?? '';
      el.value = val;
      this.toggleFilledClass(el);
    });
  }

  writeValue(value: string | null): void {
    this.digits = Array(this.length).fill('');

    if (value) {
      const chars = value.split('').slice(0, this.length);
      chars.forEach((ch, idx) => {
        this.digits[idx] = ch;
      });
    }

    setTimeout(() => this.syncDomWithDigits());
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.inputs) {
      this.inputs.forEach((ref) => {
        ref.nativeElement.disabled = isDisabled;
      });
    }
  }
}
